import type { ChargeItemDetailWithCharge } from "@/types/charge";
import {
  Package,
  PackageDetail,
  PackageFilters,
  PackageUsageFilters,
  PatientSoldPackage,
  SoldPackageDetail,
} from "@/types/inventory/package";
import { Page } from "@/types/pagination";

import {
  packageFiltersToSearchParams,
  serializePackageUsageFilters,
} from "./filter-search-params";
import { requestData } from "@/api/api-config";

export async function listPackages(
  packageFilters?: PackageFilters,
): Promise<Page<PackageDetail> | null> {
  const searchParams = packageFiltersToSearchParams(packageFilters);
  return await requestData<Page<PackageDetail>>(
    "inventory/packages/list",
    "get",
    {
      searchParams,
    },
  );
}

export async function getPackage(
  packageId: string,
): Promise<PackageDetail | null> {
  return await requestData<PackageDetail>(
    `inventory/packages/${packageId}`,
    "get",
  );
}

export async function createPackage(
  packageData: Package,
): Promise<PackageDetail | null> {
  return await requestData<PackageDetail>("inventory/packages/create", "post", {
    json: packageData,
  });
}

export async function updatePackage(
  packageId: string,
  packageUpdate: Partial<Package>,
): Promise<PackageDetail | null> {
  return await requestData<PackageDetail>(
    `inventory/packages/update/${packageId}`,
    "put",
    {
      json: packageUpdate,
    },
  );
}

export async function deletePackage(
  packageId: string,
): Promise<boolean | null> {
  return await requestData<boolean>(
    `inventory/packages/delete/${packageId}`,
    "delete",
  );
}

export async function listSoldPackages(
  patientId?: string,
  packageId?: string,
  filters?: PackageUsageFilters,
): Promise<SoldPackageDetail[] | null> {
  const searchParams = serializePackageUsageFilters(filters);
  if (patientId) searchParams.append("patient_id", patientId);
  if (packageId) searchParams.append("package_id", packageId);

  return await requestData<SoldPackageDetail[]>(
    `inventory/packages/sold`,
    "get",
    { searchParams },
  );
}

export async function listPatientSoldPackages(
  patientId: string,
  filters?: Pick<PackageUsageFilters, "status">,
): Promise<PatientSoldPackage[] | null> {
  const searchParams = serializePackageUsageFilters(filters);

  return await requestData<PatientSoldPackage[]>(
    `inventory/packages/sold/${patientId}`,
    "get",
    { searchParams },
  );
}

export async function listPackageUsage(
  packageId: string,
  filters?: PackageUsageFilters,
): Promise<SoldPackageDetail[] | null> {
  const searchParams = serializePackageUsageFilters(filters);

  return await requestData<SoldPackageDetail[]>(
    `inventory/packages/usage/${packageId}`,
    "get",
    { searchParams },
  );
}

export async function cancelSoldPackage(
  soldPackageId: string,
  cancellationReason?: string,
): Promise<boolean | null> {
  return await requestData<boolean>(
    `inventory/packages/cancel-sold-package/${soldPackageId}`,
    "put",
    {
      json: { cancellation_reason: cancellationReason },
    },
  );
}

export async function updateSoldPackageExpiry(
  soldPackageId: string,
  expiresAt: string,
): Promise<boolean | null> {
  return await requestData<boolean>(
    `inventory/packages/sold-package/${soldPackageId}/expiry`,
    "put",
    {
      json: { expires_at: expiresAt },
    },
  );
}

export async function getSoldPackageChargeItems(
  soldPackageId: string,
): Promise<ChargeItemDetailWithCharge[] | null> {
  return await requestData<ChargeItemDetailWithCharge[]>(
    `inventory/packages/sold-package/${soldPackageId}/charge-items`,
    "get",
  );
}
